// packages import
import { Box } from '@mui/material'
import { Reducer, useContext, useEffect, useReducer } from 'react'
import { Alert } from '../components/common/Alert'
// component import
import { EmptyData } from '../components/common/EmptyData'
import { KeyPressSearch } from '../components/common/KeyPressSearch'
import { AddNewCourt } from '../components/courts/AddNewCourt'
import { CourtListing } from '../components/courts/CourtListing'
import { ViewCurrentCourt } from '../components/courts/ViewCurrentCourt'
import { DeleteCourt } from '../components/courts/DeleteCourt'
// others
import { useFetchAllAssigneeCourtsLazyQuery, useFetchAllCourtsLazyQuery } from '../../generated'
import {
  CourtAction,
  CourtActionType,
  CourtState,
  courtInitialState,
  courtReducer
} from '../components/courts/CourtReducer'
import { GRAPHQL_QUERY_POLICY, NOT_FOUND_EXCEPTION, NO_COURT_FOUND, SOMETHING_WENT_WRONG } from '../constants'
import { AppContext, AuthContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import { flexCenter } from '../theme/styleConstants'
import { hasAdminAccess } from '../utils'

export const CourtsContainer = (): JSX.Element => {
  const { currentUser, isAdmin } = useContext(AuthContext)
  const { dispatch: reducerDispatch, searchValue, currentPage, pageLimit } = useContext(AppContext)
  const { id: currentUserId } = currentUser || {}
  const [courtState, courtDispatch] = useReducer<Reducer<CourtState, CourtAction>>(courtReducer, courtInitialState)
  const { allCourts } = courtState || {}

  const [fetchAllCourts, { loading, refetch }] = useFetchAllCourtsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      courtsInput: {
        paginationOptions: {
          page: currentPage,
          limit: pageLimit
        },

        searchValue: searchValue || ''
      }
    },

    onError({ message }) {
      if (message !== NOT_FOUND_EXCEPTION) {
        Alert.error(SOMETHING_WENT_WRONG)
      }

      courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: [] })
      reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      reducerDispatch({ type: ActionType.SET_FILTER_DATA_COUNT, filterDataCount: 0 })
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllCourts } = data || {}

        if (fetchAllCourts) {
          const { courts, response, pagination } = fetchAllCourts || {}
          const { status } = response || {}

          if (!courts) {
            courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: [] })
          }

          if (status === 200 && courts) {
            courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: courts })
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  const [fetchAllAssigneeCourts, { loading: assigneeLoading }] = useFetchAllAssigneeCourtsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      courtsInput: {
        paginationOptions: {
          page: currentPage,
          limit: pageLimit
        },

        searchValue: searchValue || ''
      }
    },

    onError({ message }) {
      if (message !== NOT_FOUND_EXCEPTION) {
        Alert.error(SOMETHING_WENT_WRONG)
      }

      courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: [] })
      reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      reducerDispatch({ type: ActionType.SET_FILTER_DATA_COUNT, filterDataCount: 0 })
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAssigneeCourts } = data || {}

        if (fetchAssigneeCourts) {
          const { courts, response, pagination } = fetchAssigneeCourts || {}
          const { status } = response || {}

          if (!courts) {
            courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: [] })
          }

          if (status === 200 && courts) {
            courtDispatch({ type: CourtActionType.SET_ALL_COURTS, allCourts: courts })
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  useEffect(() => {
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 }) // eslint-disable-next-line
  }, [searchValue])

  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (hasAdminAccess(currentUser)) {
      fetchAllCourts({
        variables: {
          courtsInput: {
            paginationOptions: {
              page: currentPage,
              limit: pageLimit
            },

            searchValue: searchValue || ''
          }
        }
      })
    } else {
      fetchAllAssigneeCourts({
        variables: {
          courtsInput: {
            paginationOptions: {
              page: currentPage,
              limit: pageLimit
            },
            userId: currentUserId
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [searchValue, pageLimit, currentPage])
  const isEmpty = !loading && !assigneeLoading && allCourts?.length === 0

  return (
    <>
      {isAdmin && <KeyPressSearch searchPlaceholder="Court name" />}

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 255px)' }}>
          <EmptyData title={NO_COURT_FOUND} />
        </Box>
      ) : (
        <CourtListing state={courtState} dispatch={courtDispatch} loading={loading || assigneeLoading} />
      )}
      <AddNewCourt refetch={refetch} />
      <ViewCurrentCourt state={courtState} dispatch={courtDispatch} refetch={refetch} />
      <DeleteCourt state={courtState} dispatch={courtDispatch} refetch={refetch} />
    </>
  )
}
